import type { FinPaymentRequestRow } from '@shared/api';
import { Table } from '@shared/ui/Table';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import {
  IconCheck,
  IconPause,
  IconX,
  IconForward,
  IconReply,
} from '@shared/ui/icons';
import {
  formatDateRu,
  formatMoney,
  getContractGateInfo,
  getPrimaryDocGateInfo,
  getBudgetGateInfo,
  getAccountRoutingGateInfo,
  SOURCE_MODULE_CONFIG,
  REQUEST_STATUS_CONFIG,
  PRIORITY_CONFIG,
  PAYMENT_EXECUTION_STATUS_CONFIG,
  staffInitiatorName,
} from '../financierFormat';
import {
  TableWrap,
  CheckboxTh,
  StickyRecipientTh,
  RightAlignedTh,
  RightAlignedSimpleTh,
  StickyTh,
  StickyTd,
  TableNoticeTd,
  RequestRowTr,
  RequestNumberText,
  DueDateText,
  RegNumberText,
  DocRefText,
  StaffInfoText,
  StaffCardLink,
  StickyRecipientTd,
  RecipientHeader,
  RecipientName,
  DemoBadge,
  RequisitesText,
  MonoCode,
  AccountText,
  PurposeText,
  GatesStack,
  GateChip,
  GateNotesText,
  TabularRightTd,
  InlineApprovalBox,
  InlineLabel,
  InlineInputRow,
  SmallInput,
  MiniBtn,
  CarryoverBadge,
  ApprovedAmountText,
  CarryoverRefText,
  ExecutionCell,
  ExecutionCellHeader,
  MarkPaidBtn,
  MiniProgressBar,
  MiniProgressFill,
  ExecInfoRow,
  BankDocText,
  PriorityBadgeWrap,
  RowActionsWrap,
  RowActionButton,
} from './styled';

interface RequestsTableProps {
  filtered: FinPaymentRequestRow[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  selectedIds: string[];
  allFilteredSelected: boolean;
  onToggleSelectAll: () => void;
  onToggleSelectRow: (id: string) => void;
  inlineAmounts: Record<string, number>;
  onInlineAmountChange: (id: string, val: number) => void;
  onSetPercentAmount: (id: string, base: number, pct: number) => void;
  onInlineDecide: (req: FinPaymentRequestRow, decision: 'approved' | 'deferred' | 'rejected') => void;
  onSetMarkPaidItem: (req: FinPaymentRequestRow) => void;
  onGateAlert: (info: { tone: 'info'; text: string }) => void;
  canWrite: boolean;
}

export function RequestsTable({
  filtered,
  isLoading,
  isError,
  refetch,
  selectedIds,
  allFilteredSelected,
  onToggleSelectAll,
  onToggleSelectRow,
  inlineAmounts,
  onInlineAmountChange,
  onSetPercentAmount,
  onInlineDecide,
  onSetMarkPaidItem,
  onGateAlert,
  canWrite,
}: RequestsTableProps) {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <CheckboxTh>
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={onToggleSelectAll}
                disabled={!canWrite}
                aria-label="Выбрать все заявки"
              />
            </CheckboxTh>
            <th>№ заявки / Срок</th>
            <th>Модуль</th>
            <StickyRecipientTh>Контрагент и реквизиты 1С</StickyRecipientTh>
            <th>4 гейта (Договор · Первичка · Бюджет · Счёт)</th>
            <RightAlignedTh>Запрошено / Утверждено</RightAlignedTh>
            <RightAlignedSimpleTh>Факт по выписке банка</RightAlignedSimpleTh>
            <th>Статус</th>
            {canWrite && <StickyTh>Действия</StickyTh>}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <TableNoticeTd colSpan={9}>
                Загрузка заявок на оплату…
              </TableNoticeTd>
            </tr>
          ) : isError ? (
            <tr>
              <TableNoticeTd colSpan={9}>
                Не удалось загрузить реестр заявок на оплату.{' '}
                <Button $variant="secondary" $size="sm" onClick={() => refetch()}>
                  Повторить
                </Button>
              </TableNoticeTd>
            </tr>
          ) : filtered.length === 0 ? (
            <tr>
              <TableNoticeTd colSpan={9}>
                Заявки не найдены
              </TableNoticeTd>
            </tr>
          ) : (
            filtered.map((req: FinPaymentRequestRow) => {
              const isSelected = selectedIds.includes(req.id);
              const gContract = getContractGateInfo(req.gate_contract_status);
              const gPrimary = getPrimaryDocGateInfo(req.gate_primary_doc_status);
              const gBudget = getBudgetGateInfo(req.gate_budget_status);
              const gAccount = getAccountRoutingGateInfo(req.gate_account_routing);

              const modConf = SOURCE_MODULE_CONFIG[req.source_module] ?? {
                label: req.source_module,
                tone: 'neutral',
              };
              const statusConf = REQUEST_STATUS_CONFIG[req.status] ?? {
                label: req.status,
                tone: 'neutral',
              };
              const priorityConf = PRIORITY_CONFIG[req.priority] ?? {
                label: req.priority,
                tone: 'neutral',
              };
              const execConf = PAYMENT_EXECUTION_STATUS_CONFIG[req.payment_execution_status] ?? {
                label: req.payment_execution_status === 'paid' ? 'Оплачено' : 'Не оплачено',
                tone: req.payment_execution_status === 'paid' ? 'success' : 'neutral',
              };

              const requestedAmt = req.requested_amount || req.amount;
              const rawApproved = inlineAmounts[req.id] ?? (req.approved_amount || req.amount);
              const currentApproved = Math.max(
                0,
                Math.min(requestedAmt, Number.isFinite(rawApproved) ? rawApproved : 0)
              );
              const cutAmt = Math.max(0, requestedAmt - currentApproved);

              const approvedTotal = req.approved_amount || req.amount;
              const paidAmt = req.paid_amount || 0;
              const paidPct = approvedTotal > 0 ? (paidAmt / approvedTotal) * 100 : 0;

              return (
                <RequestRowTr key={req.id} $selected={isSelected}>
                  <td>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelectRow(req.id)}
                      disabled={!canWrite}
                      aria-label={`Выбрать заявку ${req.request_number}`}
                    />
                  </td>
                  <td>
                    <RequestNumberText>{req.request_number}</RequestNumberText>
                    <DueDateText>
                      Срок: {formatDateRu(req.due_pay_date)}
                    </DueDateText>
                    {req.registry_number && (
                      <RegNumberText>
                        {req.registry_number}
                      </RegNumberText>
                    )}
                  </td>
                  <td>
                    <Badge $tone={modConf.tone}>{modConf.label}</Badge>
                    {req.source_doc_ref && (
                      <DocRefText>
                        {req.source_doc_ref}
                      </DocRefText>
                    )}
                    {/* Заявка сотрудника: кто и из какого отдела, файл счёта — в её карточке. */}
                    {req.staff_request_id && (
                      <>
                        <StaffInfoText>
                          {staffInitiatorName(req) || 'Сотрудник'}
                          {req.staff_department && ` · ${req.staff_department}`}
                        </StaffInfoText>
                        <StaffCardLink to={`/pay-requests/${req.staff_request_id}`}>
                          Счёт и карточка →
                        </StaffCardLink>
                      </>
                    )}
                  </td>
                  <StickyRecipientTd $selected={isSelected}>
                    <RecipientHeader>
                      <RecipientName>{req.counterparty_name}</RecipientName>
                      {req.id.startsWith('f1000001-') && (
                        <DemoBadge $tone="neutral">Демо-данные</DemoBadge>
                      )}
                    </RecipientHeader>
                    {req.counterparty_inn && (
                      <RequisitesText>
                        ИНН: <MonoCode>{req.counterparty_inn}</MonoCode>
                        {req.counterparty_kpp && ` / КПП: ${req.counterparty_kpp}`}
                      </RequisitesText>
                    )}
                    {req.recipient_account_number && (
                      <AccountText>
                        Р/с: <MonoCode>{req.recipient_account_number}</MonoCode>
                        {req.recipient_bik && ` (БИК ${req.recipient_bik})`}
                      </AccountText>
                    )}
                    <PurposeText>
                      {req.purpose}
                    </PurposeText>
                  </StickyRecipientTd>
                  <td>
                    <GatesStack>
                      <GateChip
                        $tone={gContract.tone}
                        title={gContract.tooltip}
                        tabIndex={0}
                        role="button"
                        aria-label={`Гейт Договор (${gContract.code}): ${gContract.tooltip}`}
                        onClick={() => onGateAlert({ tone: 'info', text: `Гейт Договор: ${gContract.tooltip}` })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onGateAlert({ tone: 'info', text: `Гейт Договор: ${gContract.tooltip}` });
                          }
                        }}
                      >
                        {gContract.code}
                      </GateChip>
                      <GateChip
                        $tone={gPrimary.tone}
                        title={gPrimary.tooltip}
                        tabIndex={0}
                        role="button"
                        aria-label={`Гейт Первичка (${gPrimary.code}): ${gPrimary.tooltip}`}
                        onClick={() => onGateAlert({ tone: 'info', text: `Гейт Первичка: ${gPrimary.tooltip}` })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onGateAlert({ tone: 'info', text: `Гейт Первичка: ${gPrimary.tooltip}` });
                          }
                        }}
                      >
                        {gPrimary.code}
                      </GateChip>
                      <GateChip
                        $tone={gBudget.tone}
                        title={gBudget.tooltip}
                        tabIndex={0}
                        role="button"
                        aria-label={`Гейт Бюджет (${gBudget.code}): ${gBudget.tooltip}`}
                        onClick={() => onGateAlert({ tone: 'info', text: `Гейт Бюджет: ${gBudget.tooltip}` })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onGateAlert({ tone: 'info', text: `Гейт Бюджет: ${gBudget.tooltip}` });
                          }
                        }}
                      >
                        {gBudget.code}
                      </GateChip>
                      <GateChip
                        $tone={gAccount.tone}
                        title={gAccount.tooltip}
                        tabIndex={0}
                        role="button"
                        aria-label={`Гейт Банк (${gAccount.code}): ${gAccount.tooltip}`}
                        onClick={() => onGateAlert({ tone: 'info', text: `Гейт Банк: ${gAccount.tooltip}` })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onGateAlert({ tone: 'info', text: `Гейт Банк: ${gAccount.tooltip}` });
                          }
                        }}
                      >
                        {gAccount.code}
                      </GateChip>
                    </GatesStack>
                    {req.gate_notes && (
                      <GateNotesText>
                        {req.gate_notes}
                      </GateNotesText>
                    )}
                  </td>

                  {/* Инлайн утверждение сумм */}
                  <TabularRightTd>
                    <InlineApprovalBox>
                      <InlineLabel>
                        Запрошено: <b>{formatMoney(requestedAmt)}</b>
                      </InlineLabel>

                      {canWrite && req.status !== 'approved' && req.status !== 'executed' ? (
                        <>
                          <InlineInputRow>
                            <SmallInput
                              type="number"
                              min={0}
                              max={requestedAmt}
                              step="0.01"
                              value={currentApproved}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                const clamped = Number.isNaN(val)
                                  ? 0
                                  : Math.max(0, Math.min(requestedAmt, val));
                                onInlineAmountChange(req.id, clamped);
                              }}
                            />
                            <MiniBtn
                              type="button"
                              onClick={() => onSetPercentAmount(req.id, requestedAmt, 1.0)}
                            >
                              100%
                            </MiniBtn>
                            <MiniBtn
                              type="button"
                              onClick={() => onSetPercentAmount(req.id, requestedAmt, 0.5)}
                            >
                              50%
                            </MiniBtn>
                            <MiniBtn
                              type="button"
                              onClick={() => onSetPercentAmount(req.id, requestedAmt, 0.3)}
                            >
                              30%
                            </MiniBtn>
                          </InlineInputRow>

                          {cutAmt > 0 && (
                            <CarryoverBadge>
                              <IconForward size={12} />
                              Срез {formatMoney(cutAmt)} переносится в -ОСТ
                            </CarryoverBadge>
                          )}
                        </>
                      ) : (
                        <ApprovedAmountText>
                          {formatMoney(req.approved_amount || req.amount)}
                          {req.cut_amount > 0 && (
                            <CarryoverBadge>
                              <IconForward size={12} />
                              Срезано в -ОСТ: {formatMoney(req.cut_amount)}
                            </CarryoverBadge>
                          )}
                        </ApprovedAmountText>
                      )}

                      {req.carryover_from_request_id && (
                        <CarryoverRefText>
                          <IconReply size={12} />
                          Перенесённый остаток из заявки ({formatMoney(requestedAmt)})
                        </CarryoverRefText>
                      )}
                    </InlineApprovalBox>
                  </TabularRightTd>

                  {/* Факт по выписке */}
                  <TabularRightTd>
                    <ExecutionCell>
                      <ExecutionCellHeader>
                        <Badge $tone={execConf.tone}>{execConf.label}</Badge>
                        {canWrite &&
                          ['approved', 'exported_1c'].includes(req.status) &&
                          req.payment_execution_status !== 'paid' &&
                          req.status !== 'executed' &&
                          (Math.round(((req.approved_amount || req.amount) - (req.paid_amount || 0)) * 100) / 100 > 0.005) && (
                            <MarkPaidBtn
                              $variant="ghost"
                              $size="sm"
                              onClick={() => onSetMarkPaidItem(req)}
                              title="Отметить оплату по выписке банка"
                            >
                              Отметить
                            </MarkPaidBtn>
                          )}
                      </ExecutionCellHeader>

                      <MiniProgressBar>
                        <MiniProgressFill $pct={paidPct} />
                      </MiniProgressBar>

                      <ExecInfoRow>
                        <span>Оплачено: {formatMoney(paidAmt)}</span>
                        <span>Остаток: {formatMoney(Math.max(0, approvedTotal - paidAmt))}</span>
                      </ExecInfoRow>

                      {req.bank_doc_numbers && (
                        <BankDocText>
                          {req.bank_doc_numbers}
                        </BankDocText>
                      )}
                    </ExecutionCell>
                  </TabularRightTd>

                  <td>
                    <Badge $tone={statusConf.tone}>{statusConf.label}</Badge>
                    <PriorityBadgeWrap>
                      <Badge $tone={priorityConf.tone}>{priorityConf.label}</Badge>
                    </PriorityBadgeWrap>
                  </td>

                  {canWrite && (
                    <StickyTd>
                      <RowActionsWrap>
                        {req.status !== 'approved' && req.status !== 'executed' && req.status !== 'exported_1c' && (
                          <RowActionButton
                            $variant="primary"
                            $size="sm"
                            onClick={() => onInlineDecide(req, 'approved')}
                            title="Проверено, подготовить Гендиректору"
                            aria-label="Проверено, подготовить Гендиректору"
                          >
                            <IconCheck size={14} />
                          </RowActionButton>
                        )}
                        {req.status !== 'deferred' && req.status !== 'executed' && req.status !== 'exported_1c' && (
                          <RowActionButton
                            $variant="secondary"
                            $size="sm"
                            onClick={() => onInlineDecide(req, 'deferred')}
                            title="Отложить"
                            aria-label="Отложить"
                          >
                            <IconPause size={14} />
                          </RowActionButton>
                        )}
                        {req.status !== 'rejected' && req.status !== 'executed' && req.status !== 'exported_1c' && (
                          <RowActionButton
                            $variant="ghost"
                            $size="sm"
                            onClick={() => onInlineDecide(req, 'rejected')}
                            title="Отклонить"
                            aria-label="Отклонить"
                          >
                            <IconX size={14} />
                          </RowActionButton>
                        )}
                      </RowActionsWrap>
                    </StickyTd>
                  )}
                </RequestRowTr>
              );
            })
          )}
        </tbody>
      </Table>
    </TableWrap>
  );
};
